/**
 * Deduplication Utility
 * Provides helper functions to remove duplicate items from arrays
 */

/**
 * Deduplicates an array of items based on a key generated by a selector function.
 * @param items The array of items to deduplicate.
 * @param keySelector A function that returns a unique key for each item (e.g., label or ID).
 * @returns A new array with duplicate items removed.
 */
export function deduplicateItems<T>(
  items: T[],
  keySelector: (item: T) => string
): T[] {
  if (!items || !Array.isArray(items)) {
    return [];
  }

  const seen = new Set<string>();
  return items.filter(item => {
    const key = keySelector(item);
    // Normalize key: trim and lowercase for loose matching
    const normalizedKey = key ? key.toString().trim().toLowerCase() : '';

    if (!normalizedKey || seen.has(normalizedKey)) {
      return false;
    }

    seen.add(normalizedKey);
    return true;
  });
}

/**
 * Deduplicates an array of strings.
 * @param items The array of strings to deduplicate.
 * @returns A new array with duplicate strings removed.
 */
export function deduplicateStrings(items: string[]): string[] {
  return deduplicateItems(items, item => item);
}
